import React, { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  changeTableSortIcons,
  copyToClipboard,
  getSortDirectionProperty,
  serverFormatToDateString,
} from "../../../../../system";
import FilterComponent from "../FilterComponent";
import ViewUserApproval from "../Modals/ViewUserApproval";
import { allSharedComponents } from "../../../../../shared";
import { UserActions } from "../../../../../redux/actions/";
import {
  PreferedLanguages,
  TableColumnStyle,
  getRoleById,
  timezoneConstants,
  translationConstants,
} from "../../../../../constants";
import Tooltip from "@mui/material/Tooltip";
import ModalApprovedRejected from "../../../../../shared/components/ModalApprovedRejected/ModalApprovedRejected";

const {
  RejectedCertificate,
  ApprovedCertificate,
  DataTable,
  CustomModal,
  ExportComponent,
} = allSharedComponents;

const defaultQueryExpression = "id != null ";

const initialGridState = {
  length: 10,
  pageNumber: 1,
  orderBy: null,
  filterQuery: defaultQueryExpression,
  search: "",
};

const ApprovalList = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const sortingInProgressRef = useRef(false);
  const [resetPage, setResetPage] = useState();
  const [totalRows, setTotalRows] = useState(0);
  const [modalContent, setModalContent] = useState(null);
  const [gridState, setGridState] = useState(initialGridState);
  const [filteredRowData, setFilteredRowData] = useState("");
  const [defaultPage, setDefaultPage] = useState(gridState.pageNumber);

  const onSetShow = () => {
    dispatch(
      UserActions.ChangeUserApprovalModalStatus(!isUserApprovalModalOpen)
    );
  };

  const approvalList = useSelector(
    (state) => state.UserReducer.approval.pageData
  );
  const totalItems = useSelector(
    (state) => state.UserReducer.approval.page.totalElements
  );
  const openAppRejModal = useSelector(
    (state) => state.UserReducer.openAppRejModal
  );

  const isUserApprovalModalOpen = useSelector(
    (x) => x.UserReducer.isUserApprovalModalOpen
  );

  const {
    userEmail,
    userID,
    fileDetails,
    eventType,
    modalStatus,
    isGeneralUser,
  } = openAppRejModal;

  const ViewUserModal = (data) => {
    dispatch(UserActions.getApprovalUserById(data.id));
    dispatch(UserActions.ChangeUserApprovalModalStatus(true));
  };

  const HandleModal = () => {
    if (eventType === "rejected") {
      setModalContent(
        <RejectedCertificate
          userID={userID}
          userEmail={userEmail}
          gridState={gridState}
          fileDetails={fileDetails}
          isGeneralUser={isGeneralUser}
        />
      );
    } else if (eventType === "approved") {
      setModalContent(
        <ApprovedCertificate
          userID={userID}
          gridState={gridState}
          userEmail={userEmail}
          fileDetails={fileDetails}
        />
      );
    }
  };

  const [columns, setColumns] = useState([
    {
      id: "id",
      name: t(translationConstants.RECORD_ID),
      cell: (row) => (
        <Tooltip
          title={t(translationConstants.COPY_TO_CLIPBOARD)}
          placement="top"
          arrow
        >
          <div
            className="truncate-text cursor-pointer"
            onClick={() => copyToClipboard(row.id)}
          >
            {row.id}
          </div>
        </Tooltip>
      ),
      reorder: true,
      sortable: true,
      sortDirection: null,
      minWidth: TableColumnStyle.COLUMN_WIDTH_120,
      maxWidth: TableColumnStyle.COLUMN_WIDTH_120,
    },
    {
      id: "englishFirstName",
      name: t(translationConstants.fullName),
      selector: (row) => `${row.englishFirstName} ${row.englishLastName}`,
      reorder: true,
      sortable: true,
      sortDirection: null,
    },
    {
      id: "arabicFirstName",
      name: t(translationConstants.nameArabic),
      selector: (row) =>
        `${row.arabicFirstName === null ? "" : row.arabicFirstName} ${
          row.arabicLastName === null ? "" : row.arabicLastName
        }`,
      reorder: true,
      sortable: true,
      sortDirection: null,
      minWidth: "140px",
    },
    {
      id: "email",
      name: t(translationConstants.Email),
      selector: (row) => row.email,
      reorder: true,
      sortable: true,
      sortDirection: null,
    },

    {
      id: "phoneNumber",
      name: t(translationConstants.phoneNumber),
      selector: (row) => row.phoneNumber,
      reorder: true,
      sortable: true,
      sortDirection: null,
      minWidth: "155px",
      cell: (row) => <div style={{ minWidth: "155px" }}>{row.phoneNumber}</div>,
    },
    {
      id: "nationality",
      name: t(translationConstants.nationality),
      selector: (row) => row.nationality,
      reorder: true,
      sortable: true,
      sortDirection: null,
      minWidth: "125px",
      cell: (row) => <div style={{ minWidth: "125px" }}>{row.nationality}</div>,
    },
    {
      id: "dob",
      name: t(translationConstants.dateOfBirth),
      // Converts timestamp to readable date format, you might need to adjust the format as per your need
      selector: (row) =>
        serverFormatToDateString(
          row.dobString,
          timezoneConstants.dayMonthYearFormat
        ),
      reorder: true,
      sortable: true,
      sortDirection: null,
      minWidth: TableColumnStyle.COLUMN_WIDTH_140,
    },
    {
      id: "gender",
      name: t(translationConstants.gender),
      selector: (row) => row.gender,
      reorder: true,
      sortable: true,
      sortDirection: null,
      cell: ({ gender }) => {
        // Assuming 0 is Male, 1 is Female, and any other value is 'Other'
        return gender === 0
          ? t(translationConstants.male)
          : gender === 1
          ? t(translationConstants.female) // Corrected the typo here
          : t(translationConstants.other); // Changed from 'active' to 'other'
      },
    },
    {
      id: "isMembershipHolder",
      name: t(translationConstants.IS_MEMEBR),
      width: "140px",
      selector: (row) =>
        row.isMembershipHolder
          ? `${t(translationConstants.YES)}`
          : `${t(translationConstants.NO)}`,
      reorder: true,
      sortable: true,
      sortDirection: null,
    },
    {
      id: "role",
      name: t(translationConstants.TYPE),
      selector: (row) => getRoleById(row.role),
      reorder: true,
      sortable: true,
      sortDirection: null,
    },
    {
      id: "countryName",
      name: t(translationConstants.countryOfResidence),
      selector: (row) => row.countryName,
      width: "195px",
      reorder: true,
      sortable: true,
      sortDirection: null,
    },
    {
      id: "cityName",
      name: t(translationConstants.cityOfResidence),
      selector: (row) => row.cityName,
      width: "170px",
      reorder: true,
      sortable: true,
      sortDirection: null,
    },
    {
      id: "preferredLangauge",
      name: t(translationConstants.PREFERRED_LANGUAGE_COLUMN_NAME),
      selector: (row) => row.preferredLangauge,
      width: "170px",
      reorder: true,
      sortable: true,
      sortDirection: null,
      cell: ({ preferredLangauge }) => {
        return preferredLangauge === PreferedLanguages.ARABIC
          ? t(translationConstants.ARABIC)
          : t(translationConstants.ENGLISH_LANGUAGE);
      },
    },
    {
      id: "approvalRequiredFor",
      name: t(translationConstants.APPLIED_FOR),
      // Converts timestamp to readable date format, you might need to adjust the format as per your need
      selector: (row) => row.approvalRequiredFor,
      reorder: true,
      sortable: true,
      sortDirection: null,
      width: "125px",
    },
    {
      id: "actions",
      name: t(translationConstants.Actions),
      cell: (row) => (
        <svg
          onClick={() => ViewUserModal(row)}
          className="table-action-btn"
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
        >
          <path
            d="M22 12C22 12 19 18 12 18C5 18 2 12 2 12C2 12 5 6 12 6C19 6 22 12 22 12Z"
            stroke="#48722D"
            strokeWidth="2"
            strokeLinecap="round"
          />
          <circle
            cx="12"
            cy="12"
            r="3"
            stroke="#48722D"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      ),
    },
  ]);

  const onSort = (sortedColumn) => {
    sortingInProgressRef.current = true;
    setColumns(changeTableSortIcons(columns, sortedColumn));
    const columnDirection = getSortDirectionProperty(sortedColumn);
    setDefaultPage(gridState.pageNumber);
    setGridState((prevState) => ({
      ...prevState,
      orderBy:
        columnDirection !== null
          ? `${sortedColumn.id} ${columnDirection}`
          : null,
    }));
  };

  const onChangePage = (pageNumber) => {
    setResetPage(pageNumber);

    if (!sortingInProgressRef.current) {
      setDefaultPage(pageNumber);

      setGridState((prevState) => ({
        ...prevState,
        pageNumber: pageNumber,
      }));
    }
  };

  const onChangeRowsPerPage = (newPerPage, pageNumber) => {
    setGridState((prevState) => ({
      ...prevState,
      length: newPerPage,
      pageNumber: pageNumber,
    }));
  };

  useEffect(() => {
    HandleModal();
    setTotalRows(totalItems);
    setFilteredRowData(approvalList);

    // eslint-disable-next-line
  }, [approvalList, openAppRejModal]);

  useEffect(() => {
    dispatch(UserActions.getApprovals(gridState));
    sortingInProgressRef.current = false;

    // eslint-disable-next-line
  }, [gridState]);

  const exportData = () => {
    let dataToSend = {
      ...gridState,
      length: 0,
      orderBy: gridState.orderBy,
    };
    dispatch(UserActions.exportUserData(dataToSend, 1));
  };

  return (
    <div className="tableContainer">
      <FilterComponent
        gridState={gridState}
        setGridState={setGridState}
        defaultQueryExpression={defaultQueryExpression}
      >
        <ExportComponent
          isServerSide
          onClick={exportData}
          filename={t(translationConstants.UserList)}
        />
      </FilterComponent>

      <DataTable
        isShowTimezone={true}
        columns={columns}
        items={filteredRowData}
        onSort={onSort}
        gridState={gridState}
        paginationTotalRows={totalRows}
        onChangeRowsPerPage={onChangeRowsPerPage}
        onChangePage={onChangePage}
        paginationDefaultPage={defaultPage}
        paginationResetDefaultPage={resetPage !== defaultPage}
      />
      <CustomModal
        scrollable={true}
        size="lg"
        setShow={onSetShow}
        show={isUserApprovalModalOpen}
        customClass="view-user-modal"
      >
        <ViewUserApproval
          gridState={gridState}
          setShow={() =>
            dispatch(UserActions.ChangeUserApprovalModalStatus(true))
          }
        />
      </CustomModal>

      {openAppRejModal.modalStatus && (
        <ModalApprovedRejected
          scrollable={true}
          size="lg"
          show={modalStatus}
          customClass="view-user-modal"
        >
          {modalContent}
        </ModalApprovedRejected>
      )}
    </div>
  );
};

export default ApprovalList;
